import { useState, useEffect, useMemo } from 'react';
import { ExternalLink, Copy, Plus, Trash2, Search as SearchIcon } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

type Discipline = 'GFX' | 'Video' | 'Lighting' | 'Audio';

type ResourceLink = {
  id: string;
  discipline: Discipline;
  title: string;
  description: string;
  url: string;
  source: string;
  type?: string;
  tags: string[];
  flags?: string[];
  isCommunity?: boolean;
  created_by?: string;
  created_at?: string;
};

export default function Resources() {
  const { profile } = useAuth();
  const [allLinks, setAllLinks] = useState<ResourceLink[]>([]);
  const [selectedDiscipline, setSelectedDiscipline] = useState<'All' | Discipline>('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOrder, setSortOrder] = useState<'curated' | 'a-z'>('curated');
  const [showAddModal, setShowAddModal] = useState(false);
  const [addModalDiscipline, setAddModalDiscipline] = useState<Discipline>('GFX');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    url: '',
    description: '',
    tags: '',
    isCurated: false,
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const isAdmin = profile?.role === 'admin';

  useEffect(() => {
    loadAllLinks();
  }, []);

  const loadAllLinks = async () => {
    try {
      const { data, error } = await supabase
        .from('resource_links')
        .select('*')
        .eq('is_approved', true)
        .order('is_curated', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;

      const normalized: ResourceLink[] = (data || []).map((row) => ({
        id: row.id,
        discipline: row.discipline,
        title: row.title,
        description: row.description || '',
        url: row.url,
        source: row.source || extractDomain(row.url),
        tags: row.tags || [],
        isCommunity: !row.is_curated,
        created_by: row.created_by,
        created_at: row.created_at,
      }));

      setAllLinks(normalized);
    } catch (error) {
      console.error('Error loading resource links:', error);
    } finally {
      setLoading(false);
    }
  };

  const extractDomain = (url: string): string => {
    try {
      const parsed = new URL(url);
      return parsed.hostname.replace('www.', '');
    } catch {
      return 'External';
    }
  };

  const allResources = useMemo(() => {
    return allLinks;
  }, [allLinks]);

  const filteredAndSorted = useMemo(() => {
    let filtered = allResources;

    if (selectedDiscipline !== 'All') {
      filtered = filtered.filter((r) => r.discipline === selectedDiscipline);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (r) =>
          r.title.toLowerCase().includes(query) ||
          r.description.toLowerCase().includes(query) ||
          r.source.toLowerCase().includes(query) ||
          r.tags.some((tag) => tag.toLowerCase().includes(query))
      );
    }

    if (sortOrder === 'a-z') {
      filtered = [...filtered].sort((a, b) => a.title.localeCompare(b.title));
    }

    return filtered;
  }, [allResources, selectedDiscipline, searchQuery, sortOrder]);

  const groupedByDiscipline = useMemo(() => {
    const grouped: Record<Discipline, { curated: ResourceLink[]; community: ResourceLink[] }> = {
      GFX: { curated: [], community: [] },
      Video: { curated: [], community: [] },
      Lighting: { curated: [], community: [] },
      Audio: { curated: [], community: [] },
    };

    filteredAndSorted.forEach((resource) => {
      if (resource.isCommunity) {
        grouped[resource.discipline].community.push(resource);
      } else {
        grouped[resource.discipline].curated.push(resource);
      }
    });

    return grouped;
  }, [filteredAndSorted]);

  const handleCopyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    } catch {
      alert('Failed to copy link');
    }
  };

  const handleAddLink = (discipline: Discipline) => {
    setAddModalDiscipline(discipline);
    setFormData({ title: '', url: '', description: '', tags: '', isCurated: false });
    setFormErrors({});
    setShowAddModal(true);
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.title.trim()) {
      errors.title = 'Title is required';
    } else if (formData.title.length > 80) {
      errors.title = 'Title must be 80 characters or less';
    }

    if (!formData.url.trim()) {
      errors.url = 'URL is required';
    } else if (!/^https?:\/\/.+/.test(formData.url)) {
      errors.url = 'URL must start with http:// or https://';
    }

    if (formData.description && formData.description.length > 140) {
      errors.description = 'Description must be 140 characters or less';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmitLink = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      if (!userData.user) throw new Error('Not authenticated');

      const tagsArray = formData.tags
        ? formData.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [];

      const { error } = await supabase.from('resource_links').insert({
        discipline: addModalDiscipline,
        title: formData.title.trim(),
        url: formData.url.trim(),
        description: formData.description.trim() || null,
        tags: tagsArray,
        source: extractDomain(formData.url.trim()),
        created_by: userData.user.id,
        is_curated: isAdmin && formData.isCurated,
      });

      if (error) throw error;

      alert('Added!');
      setShowAddModal(false);
      loadAllLinks();
    } catch (error) {
      console.error('Error adding link:', error);
      alert("Couldn't add link");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLink = async (id: string) => {
    try {
      const { error } = await supabase.from('resource_links').delete().eq('id', id);

      if (error) throw error;

      setAllLinks((prev) => prev.filter((link) => link.id !== id));
      alert('Link deleted');
    } catch (error) {
      console.error('Error deleting link:', error);
      alert('Failed to delete link');
    } finally {
      setDeleteConfirm(null);
    }
  };

  const disciplines: Discipline[] = ['GFX', 'Video', 'Lighting', 'Audio'];

  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Resources</h1>
          <p className="text-zinc-400">
            Trusted calculators + reference tools for show site. Opens in a new tab.
          </p>
        </div>

        <div className="mb-8 space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                placeholder="Search resources..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-zinc-900 border-zinc-800 text-white"
              />
            </div>
            <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as 'curated' | 'a-z')}>
              <SelectTrigger className="w-full sm:w-40 bg-zinc-900 border-zinc-800 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="curated">Curated</SelectItem>
                <SelectItem value="a-z">A–Z</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2 overflow-x-auto">
            <Button
              onClick={() => setSelectedDiscipline('All')}
              variant={selectedDiscipline === 'All' ? 'default' : 'outline'}
              className={
                selectedDiscipline === 'All'
                  ? 'bg-primary-500 text-white hover:bg-primary-600'
                  : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'
              }
            >
              All
            </Button>
            {disciplines.map((discipline) => (
              <Button
                key={discipline}
                onClick={() => setSelectedDiscipline(discipline)}
                variant={selectedDiscipline === discipline ? 'default' : 'outline'}
                className={
                  selectedDiscipline === discipline
                    ? 'bg-primary-500 text-white hover:bg-primary-600'
                    : 'border-zinc-700 text-zinc-300 hover:bg-zinc-800'
                }
              >
                {discipline}
              </Button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-zinc-400">Loading resources...</p>
          </div>
        ) : (
          <div className="space-y-12">
            {disciplines
              .filter((d) => selectedDiscipline === 'All' || selectedDiscipline === d)
              .map((discipline) => {
                const { curated, community } = groupedByDiscipline[discipline];
                if (curated.length === 0 && community.length === 0) return null;

                return (
                  <div key={discipline}>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-2xl font-semibold text-white">
                        {discipline === 'GFX' ? 'GFX OP' : discipline}
                      </h2>
                      <Button
                        onClick={() => handleAddLink(discipline)}
                        size="sm"
                        className="bg-zinc-800 text-white hover:bg-zinc-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Your Own
                      </Button>
                    </div>

                    {curated.length > 0 && (
                      <div className="mb-8">
                        <h3 className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-4">
                          Curated
                        </h3>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {curated.map((resource) => (
                            <ResourceCard
                              key={resource.id}
                              resource={resource}
                              onCopyLink={handleCopyLink}
                              onDelete={isAdmin ? () => setDeleteConfirm(resource.id) : undefined}
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {community.length > 0 && (
                      <div>
                        <h3 className="text-sm font-medium text-zinc-500 uppercase tracking-wider mb-4">
                          Community Links
                        </h3>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                          {community.map((resource) => (
                            <ResourceCard
                              key={resource.id}
                              resource={resource}
                              onCopyLink={handleCopyLink}
                              onDelete={isAdmin ? () => setDeleteConfirm(resource.id) : undefined}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-zinc-800">
          <p className="text-sm text-zinc-500 text-center">
            External links open in a new tab. Verify venue/regional RF rules and manufacturer specs.
          </p>
        </div>
      </div>

      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-zinc-900 rounded-lg border border-zinc-800 max-w-lg w-full p-6">
            <h2 className="text-xl font-semibold text-white mb-4">
              {isAdmin ? 'Add Resource' : 'Add Your Own Resource'}
            </h2>
            <div className="space-y-4">
              <div>
                <Label className="text-zinc-300">Discipline</Label>
                <Input
                  value={addModalDiscipline}
                  disabled
                  className="bg-zinc-800 border-zinc-700 text-zinc-500"
                />
              </div>
              <div>
                <Label className="text-zinc-300">
                  Title <span className="text-red-400">*</span>
                </Label>
                <Input
                  placeholder="Enter resource title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                  maxLength={80}
                />
                {formErrors.title && (
                  <p className="text-red-400 text-sm mt-1">{formErrors.title}</p>
                )}
              </div>
              <div>
                <Label className="text-zinc-300">
                  URL <span className="text-red-400">*</span>
                </Label>
                <Input
                  placeholder="https://example.com"
                  value={formData.url}
                  onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
                {formErrors.url && <p className="text-red-400 text-sm mt-1">{formErrors.url}</p>}
              </div>
              <div>
                <Label className="text-zinc-300">Description (optional)</Label>
                <Textarea
                  placeholder="Best for..."
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white resize-none"
                  rows={2}
                  maxLength={140}
                />
                <p className="text-xs text-zinc-500 mt-1">
                  {formData.description.length}/140 characters
                </p>
                {formErrors.description && (
                  <p className="text-red-400 text-sm mt-1">{formErrors.description}</p>
                )}
              </div>
              <div>
                <Label className="text-zinc-300">Tags (optional, comma-separated)</Label>
                <Input
                  placeholder="DMX, Lighting, Control"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  className="bg-zinc-800 border-zinc-700 text-white"
                />
              </div>
              {isAdmin && (
                <div className="flex items-center gap-2 p-3 bg-zinc-800/50 rounded border border-zinc-700">
                  <input
                    type="checkbox"
                    id="isCurated"
                    checked={formData.isCurated}
                    onChange={(e) => setFormData({ ...formData, isCurated: e.target.checked })}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-primary-500 focus:ring-primary-500 focus:ring-offset-0"
                  />
                  <Label htmlFor="isCurated" className="text-zinc-300 cursor-pointer">
                    Mark as curated resource (admin only)
                  </Label>
                </div>
              )}
            </div>
            <div className="flex gap-3 mt-6">
              <Button
                onClick={handleSubmitLink}
                disabled={submitting}
                className="flex-1 bg-primary-500 text-white hover:bg-primary-600"
              >
                {submitting ? 'Adding...' : 'Add Resource'}
              </Button>
              <Button
                onClick={() => setShowAddModal(false)}
                variant="outline"
                className="flex-1 border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={deleteConfirm !== null} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Resource Link?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              This will permanently delete this community resource link. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-zinc-700 text-zinc-300 hover:bg-zinc-800">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteConfirm && handleDeleteLink(deleteConfirm)}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type ResourceCardProps = {
  resource: ResourceLink;
  onCopyLink: (url: string) => void;
  onDelete?: () => void;
};

function ResourceCard({ resource, onCopyLink, onDelete }: ResourceCardProps) {
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-zinc-700 transition-colors">
      <div className="mb-3">
        <h3 className="text-white font-medium mb-1 line-clamp-2">{resource.title}</h3>
        <p className="text-sm text-zinc-400 line-clamp-2">{resource.description}</p>
      </div>

      {resource.tags.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {resource.tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className="inline-block px-2 py-0.5 bg-zinc-800 text-zinc-400 text-xs rounded"
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between text-xs text-zinc-500 mb-3">
        <span>{resource.source}</span>
        {resource.type && <span>{resource.type}</span>}
      </div>

      <div className="flex gap-2">
        <a
          href={resource.url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex-1 flex items-center justify-center gap-2 px-3 py-1.5 bg-primary-500 text-white rounded text-sm font-medium hover:bg-primary-600 transition-colors"
        >
          <ExternalLink className="w-3.5 h-3.5" />
          Open
        </a>
        <Button
          onClick={() => onCopyLink(resource.url)}
          size="sm"
          variant="outline"
          className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white"
        >
          <Copy className="w-3.5 h-3.5" />
        </Button>
        {onDelete && (
          <Button
            onClick={onDelete}
            size="sm"
            variant="outline"
            className="border-red-800 text-red-400 hover:bg-red-900/20 hover:text-red-300"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        )}
      </div>

      {resource.isCommunity && (
        <p className="text-xs text-zinc-600 mt-2">Community contribution</p>
      )}

      {resource.flags && resource.flags.length > 0 && (
        <p className="text-xs text-zinc-600 mt-1">{resource.flags.join(' • ')}</p>
      )}
    </div>
  );
}
